// import styles
import "./Anagrafica.css";

// import pacchetti
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import format from "date-fns/format";
import { it } from "date-fns/locale";
import Select from "react-select";
import Cropper from "react-easy-crop";
import Resizer from "react-image-file-resizer";
import { filesCloud } from "../../firebase/config";

// import hooks
import { useFirestore } from "../../hooks/useFirestore";
import { useDocument } from "../../hooks/useDocument";
import { useCollection } from "../../hooks/useCollection";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { useAuthContext } from "../../hooks/useAuthContext";

// import components
import ListaSpese from "../../components/ListaSpese";

// import icone
import editIcon from "../../assets/edit_icon-black.svg";
import {
  FaUser,
  FaFileContract,
  FaFolderOpen,
  FaEuroSign,
  FaTshirt,
  FaEye,
  FaArrowDown,
  FaTimes,
} from "react-icons/fa";

// import pdf generator
import { generateBadgePDF } from "../badge/BadgeGenerator";

// Documenti visibili anche agli utenti base (tutti gli altri solo agli admin)
const documentiUtenteBase = ["cartaIdentita", "codiceFiscale"];

// Array dei mesi per il filtro
const mesi = [
  { value: "tutti", label: "Tutti i mesi" },
  { value: "gennaio", label: "Gennaio" },
  { value: "febbraio", label: "Febbraio" },
  { value: "marzo", label: "Marzo" },
  { value: "aprile", label: "Aprile" },
  { value: "maggio", label: "Maggio" },
  { value: "giugno", label: "Giugno" },
  { value: "luglio", label: "Luglio" },
  { value: "agosto", label: "Agosto" },
  { value: "settembre", label: "Settembre" },
  { value: "ottobre", label: "Ottobre" },
  { value: "novembre", label: "Novembre" },
  { value: "dicembre", label: "Dicembre" },
];

// Genera automaticamente gli anni da 2022 all'anno corrente + 2
const anni = (() => {
  const annoIniziale = 2022;
  const annoCorrente = new Date().getFullYear();
  const anniTotali = [{ value: "tutti", label: "Tutti gli anni" }];

  for (let anno = annoIniziale; anno <= annoCorrente + 2; anno++) {
    anniTotali.push({
      value: anno.toString(),
      label: anno.toString(),
    });
  }

  return anniTotali;
})();

// Importa l'array dei settori da Create.js
const settori = [
  { value: "Edile", label: "Settore Edile" },
  { value: "Cucine", label: "Settore Equipment Cucine" },
  { value: "Intesa", label: "Settore Banca Intesa" },
  { value: "Elettrico", label: "Settore Elettrico" },
  { value: "FTV", label: "Settore Fotovoltaico" },
  { value: "ROBY", label: "Personale Roby" },
  { value: "Amministrazione", label: "Amministrazione" },
  { value: "Cessati", label: "Ex Dipendenti" },
  { value: "Verona", label: "Cucine Verona" },
];

const mansioni = [
  { value: "Titolare", label: "Titolare" },
  { value: "Impiegato Amministrativo", label: "Impiegato Amministrativo" },
  { value: "Impiegato Tecnico", label: "Impiegato Tecnico" },
  { value: "Responsabile Tecnico", label: "Responsabile Tecnico" },
  { value: "Operaio Elettricista", label: "Operaio Elettricista" },
  { value: "Operaio Edile", label: "Operaio Edile" },
  { value: "Manutentore Equipment", label: "Manutentore Equipment" },
  { value: "Apprendista", label: "Apprendista" },
  { value: "Operaio", label: "Operaio" },
];

const tipiStipendio = [
  { value: "Fisso", label: "Fisso" },
  { value: "Fisso+Straordinari", label: "Fisso + Straordinari" },
  { value: "Fisso+Sabato", label: "Fisso + Sabato" },
  { value: "Contratto", label: "Contratto" },
];

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

function getCroppedImg(imageSrc, pixelCrop) {
  return new Promise(async (resolve, reject) => {
    try {
      const image = await createImage(imageSrc);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        return null;
      }

      // set canvas width to final desired crop size - this will clear existing context
      canvas.width = image.width;
      canvas.height = image.height;

      // draw image
      ctx.drawImage(image, 0, 0);

      // set proper canvas dimensions for cropped image
      const data = ctx.getImageData(
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
      );

      // set canvas width to final desired crop size - this will clear existing context
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      // paste generated rotate image at the top left corner
      ctx.putImageData(data, 0, 0);

      // As Base64 string
      // return canvas.toDataURL('image/jpeg');

      // As Blob
      canvas.toBlob(
        (file) => {
          resolve(file);
        },
        "image/jpeg",
        0.75,
      );
    } catch (e) {
      reject(e);
    }
  });
}

export default function Anagrafica() {
  const navigazione = useNavigate();
  const { id } = useParams();
  const { updateDocument, response } = useFirestore("users");
  const { document: userDoc } = useDocument("users", id);
  const { user } = useAuthContext();
  const currentUserIsAdmin = useIsAdmin(user);
  const { documents: spese } = useCollection("spese");
  const { documents: vestiario } = useCollection("vestiario");
  const [speseUtente, setSpeseUtente] = useState([]);
  const [vestiarioUtente, setVestiarioUtente] = useState([]);
  const [meseFiltro, setMeseFiltro] = useState({
    value: "tutti",
    label: "Tutti i mesi",
  });
  const [annoFiltro, setAnnoFiltro] = useState({
    value: "tutti",
    label: "Tutti gli anni",
  });
  const [speseFiltrate, setSpeseFiltrate] = useState([]);

  // state per i campi del form
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [dataNascita, setDataNascita] = useState("");
  const [luogoNascita, setLuogoNascita] = useState("");
  const [codiceFiscale, setCodiceFiscale] = useState("");
  const [cittadinanza, setCittadinanza] = useState("");
  const [residenza, setResidenza] = useState("");
  const [viaResidenza, setViaResidenza] = useState("");
  const [cap, setCap] = useState("");
  const [iban, setIban] = useState("");
  const [emailGoogle, setEmailGoogle] = useState("");
  const [settore, setSettore] = useState(null); // Stato per il settore
  const [mansione, setMansione] = useState(null); // Stato per la mansione
  const [tipoStipendio, setTipoStipendio] = useState(null); // Stato per il tipo di stipendio
  const [importoFisso, setImportoFisso] = useState(""); // Stato per l'importo fisso
  const [oreViaggio, setOreViaggio] = useState(""); // Stato per le ore viaggio
  const [oreStraordinarie, setOreStraordinarie] = useState(""); // Stato per le ore straordinarie
  const [pasto, setPasto] = useState(""); // Stato per il pasto
  const [daVerificare, setDaVerificare] = useState(false); // Flag controllo 8 ore
  const [pausa, setPausa] = useState(""); // Pausa giornaliera in ore
  const [bonus, setBonus] = useState([]); // Stato per l'array dei bonus

  // State per la foto profilo
  const [foto, setFoto] = useState(null);
  const [tempFoto, setTempFoto] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const [fotoLoading, setFotoLoading] = useState(false);

  // State per i tab
  const [activeTab, setActiveTab] = useState("anagrafica");

  // State per l'anteprima PDF nel modal
  const [pdfPreview, setPdfPreview] = useState(null);

  // Funzione per ridimensionare l'immagine prima del crop
  const resizeFile = (file) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        1080,
        1080,
        "JPEG",
        75,
        0,
        (uri) => {
          resolve(uri);
        },
        "base64",
      );
    });

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      try {
        const image = await resizeFile(file);
        setTempFoto(image);
        setIsCropping(true);
        // Reset zoom and crop
        setZoom(1);
        setCrop({ x: 0, y: 0 });
      } catch (err) {
        console.log(err);
      }
    }
  };

  const onCropComplete = (_croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleSaveCrop = async () => {
    if (!croppedAreaPixels || !tempFoto) return;

    setFotoLoading(true);
    try {
      const croppedImageBlob = await getCroppedImg(tempFoto, croppedAreaPixels);

      // Upload to Firebase
      const uploadPath = `users/${id}/foto_profilo_${Date.now()}.jpg`;
      const imageRef = filesCloud.ref(uploadPath);
      await imageRef.put(croppedImageBlob);
      const imageUrl = await imageRef.getDownloadURL();

      setFoto(imageUrl);

      // Aggiorna subito il documento utente
      if (userDoc && userDoc.anagrafica) {
        await updateDocument(id, {
          "anagrafica.foto": imageUrl,
        });
      } else {
        await updateDocument(id, {
          anagrafica: { foto: imageUrl },
        });
      }

      setIsCropping(false);
      setTempFoto(null);
    } catch (e) {
      console.error(e);
      alert("Errore durante il salvataggio della foto");
    }
    setFotoLoading(false);
  };

  const handleCancelCrop = () => {
    setIsCropping(false);
    setTempFoto(null);
  };

  // Funzioni per gestire i bonus
  const handleAddBonus = () => {
    setBonus([
      ...bonus,
      {
        id: Date.now(),
        nome: "",
        descrizione: "",
        importo: "",
        scadenza: "",
      },
    ]);
  };

  const [isGeneratingBadge, setIsGeneratingBadge] = useState(false);

  const handleDownloadBadge = async () => {
    setIsGeneratingBadge(true);
    try {
      const formattedDataNascita = dataNascita
        ? format(new Date(dataNascita), "d MMM y", { locale: it })
        : "---";

      const badgeData = {
        nome: nome || "Nome",
        cognome: cognome || "Cognome",
        ruolo: mansione?.label || "Dipendente",
        codiceFiscale: codiceFiscale || "---",
        dataNascita: formattedDataNascita,
        luogoNascita: luogoNascita || "---",
        email: emailGoogle || userDoc?.email || "",
        foto: foto || "https://via.placeholder.com/150",
        id: id,
      };

      await generateBadgePDF(badgeData);
    } catch (error) {
      console.error("Errore generazione badge:", error);
      alert("Errore durante la generazione del tesserino");
    } finally {
      setIsGeneratingBadge(false);
    }
  };

  const handleBonusChange = (id, field, value) => {
    const updatedBonus = bonus.map((b) =>
      b.id === id ? { ...b, [field]: value } : b,
    );
    setBonus(updatedBonus);
  };

  const handleRemoveBonus = (id) => {
    const updatedBonus = bonus.filter((b) => b.id !== id);
    setBonus(updatedBonus);
  };

  // state per i documenti
  const [formError] = useState(null);
  const [identitaFront, setIdentitaFront] = useState(null);
  const [identitaRetro, setIdentitaRetro] = useState(null);
  const [identitaScadenza, setIdentitaScadenza] = useState(null);
  const [fiscaleFront, setFiscaleFront] = useState(null);
  const [fiscaleRetro, setFiscaleRetro] = useState(null);
  const [fiscaleScadenza, setFiscaleScadenza] = useState(null);
  const [patenteFront, setPatenteFront] = useState(null);
  const [patenteRetro, setPatenteRetro] = useState(null);
  const [patenteScadenza, setPatenteScadenza] = useState(null);

  // Nuovo stato per gestire quale campo è in modalità modifica
  const [editableField, setEditableField] = useState(null);

  // Funzione per attivare/disattivare la modalità modifica di un campo
  const toggleEdit = (field) => {
    setEditableField(editableField === field ? null : field);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const parsedPausa = String(pausa || "").trim();

    // Aggiorna l'oggetto anagrafica con tutti i campi
    const anagraficaObj = {
      nome,
      cognome,
      dataNascita: new Date(dataNascita),
      luogoNascita,
      codiceFiscale,
      cittadinanza,
      residenza,
      viaResidenza,
      cap,
      iban,
      emailGoogle,
      settore: settore ? settore.value : null, // Aggiungi il settore
      mansione: mansione ? mansione.value : null, // Aggiungi la mansione
      tipoStipendio: tipoStipendio ? tipoStipendio.value : null,
      importoFisso,
      oreViaggio,
      oreStraordinarie,
      pasto,
      daVerificare: !!daVerificare,
      pausa: parsedPausa === "" ? 0 : parseFloat(parsedPausa),
      bonus, // Aggiungi l'array dei bonus
      foto, // Aggiungi la foto
    };

    // Aggiungi i documenti solo se sono presenti
    if (identitaFront) anagraficaObj.identitaFront = identitaFront;
    if (identitaRetro) anagraficaObj.identitaRetro = identitaRetro;
    if (identitaScadenza) anagraficaObj.identitaScadenza = identitaScadenza;
    if (fiscaleFront) anagraficaObj.fiscaleFront = fiscaleFront;
    if (fiscaleRetro) anagraficaObj.fiscaleRetro = fiscaleRetro;
    if (fiscaleScadenza) anagraficaObj.fiscaleScadenza = fiscaleScadenza;
    if (patenteFront) anagraficaObj.patenteFront = patenteFront;
    if (patenteRetro) anagraficaObj.patenteRetro = patenteRetro;
    if (patenteScadenza) anagraficaObj.patenteScadenza = patenteScadenza;

    // Aggiorna il documento dell'utente
    await updateDocument(id, { anagrafica: anagraficaObj });

    if (!response.error) {
      navigazione(-1);
    }
  };

  useEffect(() => {
    if (response.success) {
      console.log("dati aggiornati");
      // navigazione("/");
    }
  }, [response.success, navigazione]);

  useEffect(() => {
    if (userDoc) {
      if (!userDoc.anagrafica) {
        setNome("");
        setCognome("");
        setDataNascita("");
        setLuogoNascita("");
        setCodiceFiscale("");
        setCittadinanza("");
        setResidenza("");
        setViaResidenza("");
        setCap("");
        setIban("");
        setEmailGoogle("");
        setDaVerificare(false);
        setPausa("");
      } else {
        setNome(userDoc.anagrafica.nome);
        setCognome(userDoc.anagrafica.cognome);
        setDataNascita(
          format(userDoc.anagrafica.dataNascita.toDate(), "yyyy-MM-dd"),
        );
        setLuogoNascita(userDoc.anagrafica.luogoNascita);
        setCodiceFiscale(userDoc.anagrafica.codiceFiscale);
        setCittadinanza(userDoc.anagrafica.cittadinanza);
        setResidenza(userDoc.anagrafica.residenza);
        setViaResidenza(userDoc.anagrafica.viaResidenza);
        setCap(userDoc.anagrafica.cap);
        setIban(userDoc.anagrafica.iban);
        setEmailGoogle(userDoc.anagrafica.emailGoogle);
        setFoto(userDoc.anagrafica.foto || null);
        setIdentitaFront(userDoc.anagrafica.identitaFront);
        setIdentitaRetro(userDoc.anagrafica.identitaRetro);
        setIdentitaScadenza(userDoc.anagrafica.identitaScadenza);
        setFiscaleFront(userDoc.anagrafica.fiscaleFront);
        setFiscaleRetro(userDoc.anagrafica.fiscaleRetro);
        setFiscaleScadenza(userDoc.anagrafica.fiscaleScadenza);
        setPatenteFront(userDoc.anagrafica.patenteFront);
        setPatenteRetro(userDoc.anagrafica.patenteRetro);
        setPatenteScadenza(userDoc.anagrafica.patenteScadenza);

        // Imposta il settore se presente
        if (userDoc.anagrafica.settore) {
          const settoreObj = settori.find(
            (s) => s.value === userDoc.anagrafica.settore,
          );
          setSettore(settoreObj || null);
        }

        // Imposta la mansione se presente
        if (userDoc.anagrafica.mansione) {
          const mansioneObj = mansioni.find(
            (m) => m.value === userDoc.anagrafica.mansione,
          );
          setMansione(mansioneObj || null);
        }

        // Imposta i dati contrattuali se presenti
        if (userDoc.anagrafica.tipoStipendio) {
          const tipoStipendioObj = tipiStipendio.find(
            (t) => t.value === userDoc.anagrafica.tipoStipendio,
          );
          setTipoStipendio(tipoStipendioObj || null);
        }
        setImportoFisso(userDoc.anagrafica.importoFisso || "");
        setOreViaggio(userDoc.anagrafica.oreViaggio || "");
        setOreStraordinarie(userDoc.anagrafica.oreStraordinarie || "");
        setPasto(userDoc.anagrafica.pasto || "");
        setDaVerificare(userDoc.anagrafica.daVerificare === true);
        setPausa(
          userDoc.anagrafica.pausa === 0 || userDoc.anagrafica.pausa
            ? String(userDoc.anagrafica.pausa)
            : "",
        );
        setBonus(userDoc.anagrafica.bonus || []); // Carica i bonus se presenti

        console.log("dati presenti");
        console.log(userDoc);
      }
    }
  }, [userDoc]);

  // Filtra le spese dell'utente quando i dati sono disponibili
  useEffect(() => {
    if (spese && id) {
      const speseFiltered = spese.filter((spesa) => spesa.autore.id === id);
      setSpeseUtente(speseFiltered);
    }
  }, [spese, id]);

  // Filtra il vestiario assegnato all'utente
  useEffect(() => {
    if (vestiario && id) {
      const vestiarioFiltered = vestiario.filter((item) => {
        return (
          item.assegnazioni &&
          item.assegnazioni.some(
            (assegnazione) => assegnazione.dipendenteId === id,
          )
        );
      });
      setVestiarioUtente(vestiarioFiltered);
    }
  }, [vestiario, id]);

  // Filtra le spese in base al mese e all'anno selezionati
  useEffect(() => {
    if (speseUtente && speseUtente.length > 0) {
      let filtrate = [...speseUtente];

      // Filtra per mese se non è "tutti"
      if (meseFiltro.value !== "tutti") {
        filtrate = filtrate.filter((spesa) => {
          const meseSpesa = format(spesa.data.toDate(), "MMMM", {
            locale: it,
          }).toLowerCase();
          return meseSpesa === meseFiltro.value;
        });
      }

      // Filtra per anno se non è "tutti"
      if (annoFiltro.value !== "tutti") {
        filtrate = filtrate.filter((spesa) => {
          const annoSpesa = format(spesa.data.toDate(), "Y", { locale: it });
          return annoSpesa === annoFiltro.value;
        });
      }

      setSpeseFiltrate(filtrate);
    } else {
      setSpeseFiltrate([]);
    }
  }, [speseUtente, meseFiltro, annoFiltro]);

  // Documenti visibili: gli utenti base vedono solo carta d'identità e codice fiscale
  const documentiVisibili = (userDoc?.documenti || []).filter(
    (doc) => currentUserIsAdmin || documentiUtenteBase.includes(doc.tipo),
  );

  return (
    <div className="anagrafica-form">
      <h2 className="page-title">
        Dati anagrafici di {nome} {cognome}
      </h2>

      {/* Tabs Header */}
      <div
        className="tabs-header"
        style={{
          display: "flex",
          gap: "20px",
          marginBottom: "30px",
          borderBottom: "1px solid #ddd",
          paddingBottom: "10px",
          overflowX: "auto",
        }}
      >
        <button
          type="button"
          onClick={() => setActiveTab("anagrafica")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "10px",
            borderBottom:
              activeTab === "anagrafica"
                ? "3px solid #1a73e8"
                : "3px solid transparent",
            color: activeTab === "anagrafica" ? "#1a73e8" : "#555",
            fontWeight: activeTab === "anagrafica" ? "bold" : "normal",
          }}
        >
          <FaUser /> Anagrafica
        </button>
        {currentUserIsAdmin && (
          <button
            type="button"
            onClick={() => setActiveTab("dati_contrattuali")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "10px",
              borderBottom:
                activeTab === "dati_contrattuali"
                  ? "3px solid #1a73e8"
                  : "3px solid transparent",
              color: activeTab === "dati_contrattuali" ? "#1a73e8" : "#555",
              fontWeight: activeTab === "dati_contrattuali" ? "bold" : "normal",
            }}
          >
            <FaFileContract /> Dati Contrattuali
          </button>
        )}
        <button
          type="button"
          onClick={() => setActiveTab("documenti")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "10px",
            borderBottom:
              activeTab === "documenti"
                ? "3px solid #1a73e8"
                : "3px solid transparent",
            color: activeTab === "documenti" ? "#1a73e8" : "#555",
            fontWeight: activeTab === "documenti" ? "bold" : "normal",
          }}
        >
          <FaFolderOpen /> Documenti
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("spese")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "10px",
            borderBottom:
              activeTab === "spese"
                ? "3px solid #1a73e8"
                : "3px solid transparent",
            color: activeTab === "spese" ? "#1a73e8" : "#555",
            fontWeight: activeTab === "spese" ? "bold" : "normal",
          }}
        >
          <FaEuroSign /> Spese
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("vestiario")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "10px",
            borderBottom:
              activeTab === "vestiario"
                ? "3px solid #1a73e8"
                : "3px solid transparent",
            color: activeTab === "vestiario" ? "#1a73e8" : "#555",
            fontWeight: activeTab === "vestiario" ? "bold" : "normal",
          }}
        >
          <FaTshirt /> Vestiario
        </button>
      </div>

      {(activeTab === "anagrafica" || activeTab === "dati_contrattuali") && (
        <form onSubmit={handleSubmit}>
          {activeTab === "anagrafica" && (
            <>
              <div
                className="foto-profilo-container"
                style={{ marginBottom: "20px", textAlign: "center" }}
              >
                <div
                  className="foto-preview"
                  style={{
                    width: "150px",
                    height: "192px", // 35mm x 45mm ratio approx
                    backgroundColor: "#f0f0f0",
                    margin: "0 auto 10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                >
                  {foto ? (
                    <img
                      src={foto}
                      alt="Foto Profilo"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <span style={{ color: "#999" }}>Nessuna foto</span>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "center",
                  }}
                >
                  <label
                    className="btn-upload-foto"
                    style={{
                      display: "inline-block",
                      padding: "8px 16px",
                      backgroundColor: "#e0e0e0",
                      cursor: "pointer",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  >
                    Galleria
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                  </label>
                  <label
                    className="btn-upload-foto"
                    style={{
                      display: "inline-block",
                      padding: "8px 16px",
                      backgroundColor: "#e0e0e0",
                      cursor: "pointer",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  >
                    Fotocamera
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                  </label>
                </div>
              </div>

              <label>
                <span>Nome:</span>
                <div className="field-container">
                  {editableField === "nome" ? (
                    <input
                      required
                      type="text"
                      onChange={(e) => setNome(e.target.value)}
                      value={nome}
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("nome")}
                      />
                      <span className="field-text">{nome}</span>
                    </div>
                  )}
                </div>
              </label>
              <label>
                <span>Cognome:</span>
                <div className="field-container">
                  {editableField === "cognome" ? (
                    <input
                      required
                      type="text"
                      onChange={(e) => setCognome(e.target.value)}
                      value={cognome}
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("cognome")}
                      />
                      <span className="field-text">{cognome}</span>
                    </div>
                  )}
                </div>
              </label>
              <label>
                <span>Data di nascita:</span>
                <div className="field-container">
                  {editableField === "dataNascita" ? (
                    <input
                      required
                      type="date"
                      onChange={(e) => setDataNascita(e.target.value)}
                      value={dataNascita}
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("dataNascita")}
                      />
                      <span className="field-text">
                        {dataNascita
                          ? format(new Date(dataNascita), "d MMMM yyyy", {
                              locale: it,
                            })
                          : ""}
                      </span>
                    </div>
                  )}
                </div>
              </label>
              <label>
                <span>Luogo di nascita:</span>
                <div className="field-container">
                  {editableField === "luogoNascita" ? (
                    <input
                      required
                      type="text"
                      onChange={(e) => setLuogoNascita(e.target.value)}
                      value={luogoNascita}
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("luogoNascita")}
                      />
                      <span className="field-text">{luogoNascita}</span>
                    </div>
                  )}
                </div>
              </label>
              <label>
                <span>Codice fiscale:</span>
                <div className="field-container">
                  {editableField === "codiceFiscale" ? (
                    <input
                      required
                      type="text"
                      onChange={(e) => setCodiceFiscale(e.target.value)}
                      value={codiceFiscale}
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("codiceFiscale")}
                      />
                      <span className="field-text">{codiceFiscale}</span>
                    </div>
                  )}
                </div>
              </label>
              <label>
                <span>Cittadinanza:</span>
                <div className="field-container">
                  {editableField === "cittadinanza" ? (
                    <input
                      required
                      type="text"
                      onChange={(e) => setCittadinanza(e.target.value)}
                      value={cittadinanza}
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("cittadinanza")}
                      />
                      <span className="field-text">{cittadinanza}</span>
                    </div>
                  )}
                </div>
              </label>
              <label>
                <span>Comune di residenza:</span>
                <div className="field-container">
                  {editableField === "residenza" ? (
                    <input
                      required
                      type="text"
                      onChange={(e) => setResidenza(e.target.value)}
                      value={residenza}
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("residenza")}
                      />
                      <span className="field-text">{residenza}</span>
                    </div>
                  )}
                </div>
              </label>
              <label>
                <span>Via di residenza:</span>
                <div className="field-container">
                  {editableField === "viaResidenza" ? (
                    <input
                      required
                      type="text"
                      onChange={(e) => setViaResidenza(e.target.value)}
                      value={viaResidenza}
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("viaResidenza")}
                      />
                      <span className="field-text">{viaResidenza}</span>
                    </div>
                  )}
                </div>
              </label>
              <label>
                <span>CAP:</span>
                <div className="field-container">
                  {editableField === "cap" ? (
                    <input
                      required
                      type="number"
                      onChange={(e) => setCap(e.target.value)}
                      value={cap}
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("cap")}
                      />
                      <span className="field-text">{cap}</span>
                    </div>
                  )}
                </div>
              </label>
              <label>
                <span>Codice IBAN:</span>
                <div className="field-container">
                  {editableField === "iban" ? (
                    <input
                      required
                      type="text"
                      onChange={(e) => setIban(e.target.value)}
                      value={iban}
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("iban")}
                      />
                      <span className="field-text">{iban}</span>
                    </div>
                  )}
                </div>
              </label>
              <label>
                <span>Email Google per calendario aziendale:</span>
                <div className="field-container">
                  {editableField === "emailGoogle" ? (
                    <input
                      required
                      type="email"
                      onChange={(e) => setEmailGoogle(e.target.value)}
                      value={emailGoogle}
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("emailGoogle")}
                      />
                      <span className="field-text">{emailGoogle}</span>
                    </div>
                  )}
                </div>
              </label>
              <label>
                <span>Settore:</span>
                <div className="field-container">
                  {editableField === "settore" ? (
                    <Select
                      options={settori}
                      onChange={(option) => setSettore(option)}
                      value={settore}
                      placeholder="Seleziona un settore"
                      isClearable
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("settore")}
                      />
                      <span className="field-text">
                        {settore ? settore.label : "Nessun settore assegnato"}
                      </span>
                    </div>
                  )}
                </div>
              </label>
            </>
          )}

          {activeTab === "dati_contrattuali" && currentUserIsAdmin && (
            <div
              className="dati-contrattuali-section"
              style={{
                marginTop: "30px",
                paddingTop: "20px",
              }}
            >
              <h3 style={{ marginBottom: "15px", color: "#333" }}>
                Dati Contrattuali
              </h3>

              <label>
                <span>Mansione:</span>
                <div className="field-container">
                  {editableField === "mansione" ? (
                    <Select
                      options={mansioni}
                      onChange={(option) => setMansione(option)}
                      value={mansione}
                      placeholder="Seleziona una mansione"
                      isClearable
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("mansione")}
                      />
                      <span className="field-text">
                        {mansione
                          ? mansione.label
                          : "Nessuna mansione assegnata"}
                      </span>
                    </div>
                  )}
                </div>
              </label>

              <label>
                <span>Tipologia Stipendio:</span>
                <div className="field-container">
                  {editableField === "tipoStipendio" ? (
                    <Select
                      options={tipiStipendio}
                      onChange={(option) => setTipoStipendio(option)}
                      value={tipoStipendio}
                      placeholder="Seleziona tipologia"
                      isClearable
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("tipoStipendio")}
                      />
                      <span className="field-text">
                        {tipoStipendio
                          ? tipoStipendio.label
                          : "Non specificato"}
                      </span>
                    </div>
                  )}
                </div>
              </label>

              <label>
                <span>Importo Fisso (€):</span>
                <div className="field-container">
                  {editableField === "importoFisso" ? (
                    <input
                      type="number"
                      step="0.01"
                      onChange={(e) => setImportoFisso(e.target.value)}
                      value={importoFisso}
                      placeholder="0.00"
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("importoFisso")}
                      />
                      <span className="field-text">
                        {importoFisso ? `€ ${importoFisso}` : "Non specificato"}
                      </span>
                    </div>
                  )}
                </div>
              </label>

              <label>
                <span>Ore Viaggio (€/h):</span>
                <div className="field-container">
                  {editableField === "oreViaggio" ? (
                    <input
                      type="number"
                      step="0.01"
                      onChange={(e) => setOreViaggio(e.target.value)}
                      value={oreViaggio}
                      placeholder="0.00"
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("oreViaggio")}
                      />
                      <span className="field-text">
                        {oreViaggio ? `€ ${oreViaggio}` : "Non specificato"}
                      </span>
                    </div>
                  )}
                </div>
              </label>

              <label>
                <span>Ore Straordinarie (€/h):</span>
                <div className="field-container">
                  {editableField === "oreStraordinarie" ? (
                    <input
                      type="number"
                      step="0.01"
                      onChange={(e) => setOreStraordinarie(e.target.value)}
                      value={oreStraordinarie}
                      placeholder="0.00"
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("oreStraordinarie")}
                      />
                      <span className="field-text">
                        {oreStraordinarie
                          ? `€ ${oreStraordinarie}`
                          : "Non specificato"}
                      </span>
                    </div>
                  )}
                </div>
              </label>

              <label>
                <span>Pasto (€):</span>
                <div className="field-container">
                  {editableField === "pasto" ? (
                    <input
                      type="number"
                      step="0.01"
                      onChange={(e) => setPasto(e.target.value)}
                      value={pasto}
                      placeholder="0.00"
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("pasto")}
                      />
                      <span className="field-text">
                        {pasto ? `€ ${pasto}` : "Non specificato"}
                      </span>
                    </div>
                  )}
                </div>
              </label>

              <label>
                <span>Verifica 8 ore:</span>
                <div className="field-container">
                  {editableField === "daVerificare" ? (
                    <input
                      type="checkbox"
                      checked={daVerificare}
                      onChange={(e) => setDaVerificare(e.target.checked)}
                      style={{ width: "20px", height: "20px" }}
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("daVerificare")}
                      />
                      <span className="field-text">
                        {daVerificare ? "Attivo" : "Disattivo"}
                      </span>
                    </div>
                  )}
                </div>
              </label>

              <label>
                <span>Pausa (ore):</span>
                <div className="field-container">
                  {editableField === "pausa" ? (
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      onChange={(e) => setPausa(e.target.value)}
                      value={pausa}
                      placeholder="0"
                    />
                  ) : (
                    <div className="text-with-edit">
                      <img
                        src={editIcon}
                        alt="Modifica"
                        className="edit-icon"
                        onClick={() => toggleEdit("pausa")}
                      />
                      <span className="field-text">
                        {pausa !== "" ? `${pausa} h` : "Non specificata"}
                      </span>
                    </div>
                  )}
                </div>
              </label>

              <div className="bonus-section" style={{ marginTop: "20px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <h4 style={{ margin: 0, color: "#555" }}>Bonus</h4>
                  <button
                    type="button"
                    onClick={handleAddBonus}
                    style={{
                      backgroundColor: "#4caf50",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "30px",
                      height: "30px",
                      fontSize: "20px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    +
                  </button>
                </div>

                {bonus.map((item) => (
                  <div
                    key={item.id}
                    className="bonus-item"
                    style={{
                      border: "1px solid #eee",
                      padding: "10px",
                      borderRadius: "4px",
                      marginBottom: "10px",
                      position: "relative",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleRemoveBonus(item.id)}
                      style={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        background: "none",
                        border: "none",
                        color: "#ff5252",
                        cursor: "pointer",
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    >
                      ×
                    </button>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        <span style={{ fontSize: "12px", color: "#777" }}>
                          Nome
                        </span>
                        <input
                          type="text"
                          value={item.nome}
                          onChange={(e) =>
                            handleBonusChange(item.id, "nome", e.target.value)
                          }
                          placeholder="Nome bonus"
                          style={{
                            padding: "5px",
                            border: "1px solid #ccc",
                            borderRadius: "3px",
                          }}
                        />
                      </label>
                      <label
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        <span style={{ fontSize: "12px", color: "#777" }}>
                          Importo (€)
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          value={item.importo}
                          onChange={(e) =>
                            handleBonusChange(
                              item.id,
                              "importo",
                              e.target.value,
                            )
                          }
                          placeholder="0.00"
                          style={{
                            padding: "5px",
                            border: "1px solid #ccc",
                            borderRadius: "3px",
                          }}
                        />
                      </label>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        <span style={{ fontSize: "12px", color: "#777" }}>
                          Descrizione
                        </span>
                        <input
                          type="text"
                          value={item.descrizione}
                          onChange={(e) =>
                            handleBonusChange(
                              item.id,
                              "descrizione",
                              e.target.value,
                            )
                          }
                          placeholder="Descrizione breve"
                          style={{
                            padding: "5px",
                            border: "1px solid #ccc",
                            borderRadius: "3px",
                          }}
                        />
                      </label>
                      <label
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        <span style={{ fontSize: "12px", color: "#777" }}>
                          Scadenza
                        </span>
                        <input
                          type="date"
                          value={item.scadenza}
                          onChange={(e) =>
                            handleBonusChange(
                              item.id,
                              "scadenza",
                              e.target.value,
                            )
                          }
                          style={{
                            padding: "5px",
                            border: "1px solid #ccc",
                            borderRadius: "3px",
                          }}
                        />
                      </label>
                    </div>
                  </div>
                ))}
                {bonus.length === 0 && (
                  <p
                    style={{
                      color: "#999",
                      fontStyle: "italic",
                      fontSize: "14px",
                    }}
                  >
                    Nessun bonus assegnato
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === "anagrafica" && (
            <button
              type="button"
              className="btn"
              onClick={handleDownloadBadge}
              disabled={isGeneratingBadge}
              style={{
                backgroundColor: "#607d8b",
                marginRight: "10px",
              }}
            >
              {isGeneratingBadge ? "Generazione..." : "Scarica Tesserino"}
            </button>
          )}
          <button className="btn">Aggiorna dati</button>
          {formError && <p className="error">{formError}</p>}
        </form>
      )}

      {activeTab === "documenti" && (
        <div className="documenti-section" style={{ marginTop: "20px" }}>
          <h3>Documenti</h3>
          {documentiVisibili.length > 0 ? (
            <div className="documenti-list">
              {documentiVisibili.map((doc) => (
                <div key={doc.tipo} className="documento-row">
                  <div className="documento-info">
                    <span className="documento-label">
                      {doc.label || doc.tipo}
                    </span>
                    <span className="documento-scadenza">
                      Scadenza:{" "}
                      {doc.indeterminato
                        ? "Indeterminato"
                        : doc.scadenza
                          ? format(new Date(doc.scadenza), "d MMMM yyyy", {
                              locale: it,
                            })
                          : "non specificata"}
                    </span>
                  </div>
                  {doc.url && (
                    <div className="documento-actions">
                      <button
                        type="button"
                        className="documento-btn documento-btn-view"
                        onClick={() =>
                          setPdfPreview({
                            url: doc.url,
                            label: doc.label || doc.tipo,
                          })
                        }
                        title="Visualizza"
                        aria-label="Visualizza"
                      >
                        <FaEye />
                      </button>
                      <a
                        className="documento-btn documento-btn-download"
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Scarica"
                        aria-label="Scarica"
                      >
                        <FaArrowDown />
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p>Nessun documento caricato</p>
          )}
        </div>
      )}

      {activeTab === "spese" && (
        <div className="spese-utente-section" style={{ marginTop: "20px" }}>
          <h3>Spese dell'utente</h3>
          {speseUtente && speseUtente.length > 0 ? (
            <>
              <div className="filtri-container">
                <div className="filtro-mese">
                  <label>
                    <span>Filtra per mese:</span>
                    <Select
                      onChange={(option) => setMeseFiltro(option)}
                      options={mesi}
                      value={meseFiltro}
                      isSearchable={false}
                      className="select-filtro"
                    />
                  </label>
                </div>
                <div className="filtro-anno">
                  <label>
                    <span>Filtra per anno:</span>
                    <Select
                      onChange={(option) => setAnnoFiltro(option)}
                      options={anni}
                      value={annoFiltro}
                      isSearchable={false}
                      className="select-filtro"
                    />
                  </label>
                </div>
              </div>
              <div className="spese-list">
                <p>
                  Totale spese: {speseFiltrate.length} - Importo totale: €
                  {speseFiltrate.length > 0
                    ? speseFiltrate
                        .reduce(
                          (acc, spesa) => acc + parseFloat(spesa.importo),
                          0,
                        )
                        .toFixed(2)
                    : "0.00"}
                </p>
                {/* Utilizziamo il componente ListaSpese invece del rendering manuale */}
                {speseFiltrate.length > 0 ? (
                  <ListaSpese data={speseFiltrate} visible={true} />
                ) : (
                  <p>Nessuna spesa trovata con i filtri selezionati</p>
                )}
              </div>
            </>
          ) : (
            <p>Nessuna spesa disponibile per questo utente</p>
          )}
        </div>
      )}

      {activeTab === "vestiario" && (
        <div className="vestiario-utente-section" style={{ marginTop: "20px" }}>
          <h3>Vestiario Assegnato</h3>
          {vestiarioUtente && vestiarioUtente.length > 0 ? (
            <div className="vestiario-list">
              {vestiarioUtente.map((item) => {
                // Trova le assegnazioni per questo utente
                const assegnazioniUtente = item.assegnazioni.filter(
                  (assegnazione) => assegnazione.dipendenteId === id,
                );

                return (
                  <div key={item.id} className="vestiario-card">
                    <div className="vestiario-info">
                      <h4>{item.tipo}</h4>
                      <p>
                        <strong>Taglia:</strong> {item.taglia}
                      </p>
                      <p>
                        <strong>Colore:</strong> {item.colore}
                      </p>
                      <p>
                        <strong>Marca:</strong> {item.marca}
                      </p>
                      {item.descrizione && (
                        <p>
                          <strong>Descrizione:</strong> {item.descrizione}
                        </p>
                      )}
                    </div>
                    <div className="assegnazioni-info">
                      <h5>Assegnazioni:</h5>
                      {assegnazioniUtente.map((assegnazione, index) => (
                        <div key={index} className="assegnazione-item">
                          <p>
                            <strong>Quantità:</strong> {assegnazione.quantita}
                          </p>
                          <p>
                            <strong>Data assegnazione:</strong>{" "}
                            {format(
                              assegnazione.dataAssegnazione.toDate
                                ? assegnazione.dataAssegnazione.toDate()
                                : new Date(assegnazione.dataAssegnazione),
                              "d MMMM yyyy",
                              { locale: it },
                            )}
                          </p>
                          {assegnazione.note && (
                            <p>
                              <strong>Note:</strong> {assegnazione.note}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p>Nessun vestiario assegnato</p>
          )}
        </div>
      )}

      {/* Modal Crop */}
      {isCropping && (
        <div
          className="crop-modal"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="crop-container"
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "500px",
              height: "500px",
              backgroundColor: "#333",
              marginBottom: "20px",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Cropper
              image={tempFoto}
              crop={crop}
              zoom={zoom}
              aspect={35 / 45}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </div>
          <div
            className="controls"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
              width: "90%",
              maxWidth: "400px",
            }}
          >
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              aria-labelledby="Zoom"
              onChange={(e) => setZoom(e.target.value)}
              className="zoom-range"
              style={{ width: "100%" }}
            />
            <div className="buttons" style={{ display: "flex", gap: "15px" }}>
              <button
                onClick={handleCancelCrop}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Annulla
              </button>
              <button
                onClick={handleSaveCrop}
                disabled={fotoLoading}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#b71c1c",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  opacity: fotoLoading ? 0.7 : 1,
                }}
              >
                {fotoLoading ? "Salvataggio..." : "Salva e Taglia"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Anteprima PDF */}
      {pdfPreview && (
        <div className="pdf-preview-modal" onClick={() => setPdfPreview(null)}>
          <div
            className="pdf-preview-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pdf-preview-header">
              <h3>{pdfPreview.label}</h3>
              <button
                type="button"
                className="pdf-preview-close"
                onClick={() => setPdfPreview(null)}
                title="Chiudi"
                aria-label="Chiudi"
              >
                <FaTimes />
              </button>
            </div>
            <iframe
              src={pdfPreview.url}
              title={pdfPreview.label}
              className="pdf-preview-frame"
            />
          </div>
        </div>
      )}
    </div>
  );
}
